import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import {
  Loader2, Search, SlidersHorizontal, Calendar, Users, User, BookOpen,
  ShoppingCart, IndianRupee, Star, Smartphone, Download, Gift, MessageSquare,
  ListChecks, Activity, AlertCircle, FileText,
} from 'lucide-react';
import { fmtINR } from './AdminList';

const secCls = 'bg-white border border-fab-primary-lighter rounded-xl p-5';
const boxCls = 'bg-white border border-fab-primary/40 rounded-lg px-4 py-3 min-w-0';
const kCls = 'text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold';
const vCls = 'font-display text-xl font-bold text-fab-text mt-1';

const PERIODS = [
  ['today', 'Today'], ['week', 'Weekly'], ['month', 'Monthly'],
  ['quarter', 'Quarterly'], ['year', 'Yearly'],
];

const nf = (n) => (n ?? 0).toLocaleString('en-IN');

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    api.get('/admin/dashboard-stats')
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e?.response?.data?.detail || 'load failed'));
  }, []);

  if (err) return <div className="text-red-600 text-[13px]" data-testid="admin-dashboard-error">{err}</div>;
  if (!d) return <div className="text-fab-text-muted text-[13px]" data-testid="admin-dashboard-loading"><Loader2 className="inline w-4 h-4 animate-spin" /> loading platform overview…</div>;

  const s = d.sellers, b = d.buyers, oc = d.orders_count, oa = d.order_amount, conv = d.conversations;

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      {/* Global search / filter bar */}
      <div className="bg-white border border-fab-primary-lighter rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="admin-global-search-bar">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 text-fab-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="admin-global-search-input"
            placeholder="Search sellers, buyers, orders, products, conversations…"
            className="w-full pl-9 pr-3 py-2 text-[13px] rounded-lg border border-fab-primary-lighter bg-fab-bg-subtle/40 focus:outline-none focus:ring-2 focus:ring-fab-primary/30"
          />
        </div>
        <select data-testid="admin-entity-filter" className="px-3 py-2 text-[13px] rounded-lg border border-fab-primary-lighter bg-white text-fab-text-secondary">
          <option>All Entities</option><option>Sellers</option><option>Buyers</option>
          <option>Orders</option><option>Products</option><option>Conversations</option>
        </select>
        <div className="flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-lg border border-fab-primary-lighter text-fab-text-secondary">
          <Calendar className="w-4 h-4 text-fab-primary" />
          <input type="date" data-testid="admin-date-from" className="bg-transparent focus:outline-none text-[12px]" />
          <span className="text-fab-text-muted">–</span>
          <input type="date" data-testid="admin-date-to" className="bg-transparent focus:outline-none text-[12px]" />
        </div>
        <button data-testid="admin-filter-btn" className="flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-lg border border-fab-primary text-fab-primary font-semibold hover:bg-fab-primary/5">
          <SlidersHorizontal className="w-4 h-4" /> Filter
        </button>
      </div>

      {/* 1. Sellers */}
      <Section n="1" title="Sellers" icon={Users}>
        <div className="text-[11px] font-semibold text-fab-text-secondary mb-2">TOTAL ACTIVE SELLERS <span className="text-fab-text-muted font-normal">(Excluding Suspended)</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Box k="Standard Plan" v={nf(s.by_plan.standard)} accent="text-fab-primary" />
          <Box k="Pro Plan" v={nf(s.by_plan.pro)} accent="text-emerald-600" />
          <Box k="Enterprise Plan" v={nf(s.by_plan.enterprise)} accent="text-indigo-600" />
          <Box k="Free Trial" v={nf(s.by_plan.free_trial)} accent="text-amber-600" />
          <Box k="Suspended Accounts" v={nf(s.suspended)} accent="text-red-500" tone="danger" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Box k="Total Sellers" v={nf(s.total)} big />
          <Box k="Deactivated Sellers" v={nf(s.deactivated)} accent="text-red-500" big />
        </div>
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-fab-text-secondary mb-2">NEW SELLERS</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Box k="Today" v={nf(s.new.today)} center />
            <Box k="Weekly" v={nf(s.new.week)} center />
            <Box k="Monthly" v={nf(s.new.month)} center />
            <Box k="Quarterly" v={nf(s.new.quarter)} center />
          </div>
        </div>
      </Section>

      {/* 2. Buyers */}
      <Section n="2" title="Buyers" icon={User}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Box k="Total Buyers" v={nf(b.total)} accent="text-fab-primary" big />
          <Box k="Repeat Buyers" v={nf(b.repeat)} accent="text-fab-primary" big />
          <Box k="Deactivated Buyers" v={b.deactivated?.available === false ? '—' : nf(b.deactivated)} accent="text-red-500" big note={b.deactivated?.available === false ? 'Not tracked (no buyer block flag)' : null} />
        </div>
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-fab-text-secondary mb-2">NEW BUYERS</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {PERIODS.map(([p, label]) => (<Box key={p} k={label} v={nf(b.new[p])} center />))}
          </div>
        </div>
      </Section>

      {/* 3. Total Catalogues */}
      <Section n="3" title="Total Catalogues" icon={BookOpen}>
        <div className="flex items-center justify-between">
          <div><div className="font-display text-3xl font-bold text-fab-text">{nf(d.catalogues)}</div><div className="text-[11px] text-fab-text-muted">Total Catalogues</div></div>
          <div className="w-11 h-11 rounded-full bg-fab-primary/10 flex items-center justify-center"><FileText className="w-5 h-5 text-fab-primary" /></div>
        </div>
      </Section>

      {/* 4. Total Orders */}
      <Section n="4" title="Total Orders" icon={ShoppingCart}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PERIODS.map(([p, label]) => (<Box key={p} k={label === 'Weekly' ? 'Last Week' : label} v={nf(oc[p])} center />))}
        </div>
      </Section>

      {/* 5. Total Order Amount */}
      <Section n="5" title="Total Order Amount" icon={IndianRupee}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PERIODS.map(([p, label]) => (<Box key={p} k={label === 'Weekly' ? 'Last Week' : label} v={fmtINR(oa[p])} center accent="text-fab-text" />))}
        </div>
      </Section>

      {/* 6. Ratings */}
      <Section n="6" title="Ratings" icon={Star}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className={boxCls}>
            <div className={kCls}>Average Platform Rating</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-display text-3xl font-bold text-fab-text">{d.ratings.average || '—'}</span>
              <Stars value={d.ratings.average} />
            </div>
          </div>
          <Box k="Total Ratings" v={nf(d.ratings.total)} big />
        </div>
      </Section>

      {/* 7. App Downloads & Push Notifications */}
      <Section n="7" title="App Downloads & Push Notifications" icon={Smartphone}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <div className="text-[11px] font-semibold text-fab-primary mb-2">APP DOWNLOADS</div>
            <div className="text-[12px] text-fab-text-muted bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Not tracked — no app-install/download event is recorded yet.</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-fab-primary mb-2">PUSH SUBSCRIPTIONS</div>
            <div className="grid grid-cols-3 gap-2">
              <Box k="Buyer" v={nf(d.push.subscriptions.buyer)} />
              <Box k="Seller" v={nf(d.push.subscriptions.seller)} />
              <Box k="Total" v={nf(d.push.subscriptions.total)} accent="text-fab-primary" />
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-fab-primary mb-2">DELIVERY (SENT / DELIVERED / FAILED)</div>
            <div className="text-[12px] text-fab-text-muted bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Not tracked — push is sent best-effort; no send/delivery/failure records are stored.</div>
          </div>
        </div>
      </Section>

      {/* 8. MRR */}
      <Section n="8" title="MRR (SaaS)" icon={IndianRupee}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[12.5px] text-fab-text-muted bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex-1">
            MRR unavailable — <b>no subscription plan pricing or GST rate is stored</b> in the database. Base Price + GST = Total MRR cannot be computed without a pricing/GST source.
          </div>
          <button disabled data-testid="admin-mrr-pdf" className="flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-lg border border-fab-primary-lighter text-fab-text-muted cursor-not-allowed">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </Section>

      {/* 9. Affiliate Marketing */}
      <Section n="9" title="Affiliate Marketing" icon={Gift}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[12.5px] text-fab-text-muted bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex-1">
            No affiliate/marketer/commission data model exists yet — Revenue, Active Marketers, Sales and Commission Payable cannot be shown without that system.
          </div>
          <button disabled data-testid="admin-affiliate-pdf" className="flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-lg border border-fab-primary-lighter text-fab-text-muted cursor-not-allowed">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </Section>

      {/* 10. Total Conversations */}
      <Section n="10" title="Total Conversations" icon={MessageSquare} sub="Buyer + Seller">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PERIODS.map(([p, label]) => (
            <div key={p} className={`${boxCls} text-center`}>
              <div className={kCls}>{label}</div>
              <div className={vCls}>{nf(conv[p].total)}</div>
              <div className="text-[9.5px] text-fab-text-muted mt-0.5">B {nf(conv[p].buyer)} · S {nf(conv[p].seller)}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 11. Admin Log */}
      <Section n="11" title="Admin Log" icon={ListChecks} sub="Recent activity">
        {d.admin_log.length === 0 ? (
          <div className="text-[12.5px] text-fab-text-muted">No admin actions recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="admin-log-table">
              <thead><tr className="text-fab-text-muted text-left border-b border-fab-primary-lighter">
                <th className="py-2 pr-3">Date & Time</th><th className="pr-3">Admin</th><th className="pr-3">Action</th><th className="pr-3">Entity</th><th className="pr-3">Details</th><th className="pr-3">IP</th></tr></thead>
              <tbody>
                {d.admin_log.map((l, i) => (
                  <tr key={i} className="border-b border-fab-primary-lighter/50">
                    <td className="py-2 pr-3 whitespace-nowrap text-fab-text-muted">{fmtDate(l.created_at || l.at)}</td>
                    <td className="pr-3">{l.actor_email || l.admin || l.actor_name || 'Admin'}</td>
                    <td className="pr-3">{l.action || l.event || '—'}</td>
                    <td className="pr-3">{l.entity || l.target || l.target_label || '—'}</td>
                    <td className="pr-3">{l.detail || l.details || (l.old_value != null ? `${l.old_value} → ${l.new_value}` : '—')}</td>
                    <td className="pr-3 text-fab-text-muted">{l.ip || l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 12. Platform Health */}
      <Section n="12" title="Platform Health" icon={Activity} sub="System status">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[['Backend', 'backend'], ['Database', 'database'], ['Storage', 'storage'], ['WhatsApp API', 'whatsapp'], ['AI / LLM', 'ai_llm'], ['Notifications', 'notifications'], ['Payments', 'payments']].map(([label, key]) => (
            <div key={key} className={`${boxCls} text-center`}><div className={kCls}>{label}</div><HealthChip status={d.health[key]} /></div>
          ))}
        </div>
        <div className="flex flex-wrap gap-6 mt-3 text-[12px]">
          <div><span className="text-fab-text-muted">Last Deployment: </span><b>{d.health.last_deployment?.available === false ? 'Not recorded' : fmtDate(d.health.last_deployment)}</b></div>
          <div><span className="text-fab-text-muted">Version: </span><b>{d.health.version}</b></div>
        </div>
      </Section>

      {/* 13. Error / Issue Centre */}
      <Section n="13" title="Error / Issue Centre" icon={AlertCircle} sub="Recent">
        {d.error_centre.items.length === 0 ? (
          <div className="text-[12.5px] text-fab-text-muted">No recent failures detected.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="admin-error-table">
              <thead><tr className="text-fab-text-muted text-left border-b border-fab-primary-lighter">
                <th className="py-2 pr-3">Timestamp</th><th className="pr-3">Type</th><th className="pr-3">Seller</th><th className="pr-3">Buyer</th><th className="pr-3">Order ID</th><th className="pr-3">Details</th></tr></thead>
              <tbody>
                {d.error_centre.items.map((e, i) => (
                  <tr key={i} className="border-b border-fab-primary-lighter/50">
                    <td className="py-2 pr-3 whitespace-nowrap text-fab-text-muted">{fmtDate(e.timestamp)}</td>
                    <td className="pr-3"><span className="text-red-500 font-medium">{e.type}</span></td>
                    <td className="pr-3">{e.seller_id || '—'}</td>
                    <td className="pr-3">{e.buyer || '—'}</td>
                    <td className="pr-3">{e.order_id || '—'}</td>
                    <td className="pr-3">{e.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[10.5px] text-fab-text-muted mt-2">{d.error_centre.note}</div>
      </Section>

      <div className="text-[10.5px] text-fab-text-muted">Generated {new Date(d.generated_at).toLocaleString('en-IN')}</div>
    </div>
  );
}

function Section({ n, title, sub, icon: Icon, children }) {
  return (
    <div className={secCls} data-testid={`admin-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-fab-primary" />
        <span className="text-[13px] font-bold text-fab-text">{n}. {title}</span>
        {sub && <span className="text-[10.5px] text-fab-text-muted uppercase tracking-wide">({sub})</span>}
      </div>
      {children}
    </div>
  );
}

function Box({ k, v, accent = 'text-fab-text', big, center, tone, note }) {
  return (
    <div className={`${boxCls} ${center ? 'text-center' : ''} ${tone === 'danger' ? 'bg-red-50/50 border-red-200' : ''}`} data-testid={`admin-metric-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className={kCls}>{k}</div>
      <div className={`font-display font-bold mt-1 ${big ? 'text-2xl' : 'text-xl'} ${accent}`}>{v ?? '—'}</div>
      {note && <div className="text-[9.5px] text-fab-text-muted mt-0.5">{note}</div>}
    </div>
  );
}

function Stars({ value = 0 }) {
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-fab-primary-lighter'}`} />
      ))}
    </span>
  );
}

function HealthChip({ status }) {
  const map = {
    healthy: ['Healthy', 'text-emerald-600', 'bg-emerald-500'],
    warning: ['Warning', 'text-amber-600', 'bg-amber-500'],
    error: ['Error', 'text-red-600', 'bg-red-500'],
    unknown: ['Unknown', 'text-fab-text-muted', 'bg-fab-text-muted'],
  };
  const [label, txt, dot] = map[status] || map.unknown;
  return (
    <div className={`flex items-center justify-center gap-1.5 mt-1.5 text-[12px] font-semibold ${txt}`}>
      <span className={`w-2 h-2 rounded-full ${dot}`} /> {label}
    </div>
  );
}

function fmtDate(v) {
  if (!v || typeof v !== 'string') return '—';
  const dt = new Date(v);
  return isNaN(dt) ? '—' : dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
